/**
 * Audit log query and export (`app/services/audit_log_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';
import { assertPojo } from '@brainfocus/web/api/response';

// ========== Audit Logs API (SuperAdmin only) ==========

/**
 * Get paginated audit logs (SuperAdmin only)
 */
export async function getAuditLogs(filters: AuditLogFilters): Promise<AuditLogResponse> {
    const response = await client.request('POST', '/api/audit-logs', {
        page: filters.page,
        per_page: filters.perPage,
        event_type: filters.eventType,
        event_category: filters.eventCategory,
        user_id: filters.userId,
        institution_id: filters.institutionId,
        start_date: filters.startDate,
        end_date: filters.endDate,
        success: filters.success,
    });
    assertPojo<AuditLogResponse>(response);
    return response;
}

/**
 * Export audit logs as CSV (SuperAdmin only)
 */
export async function exportAuditLogsCsv(filters: AuditLogFilters): Promise<Blob> {
    const response = await client.request('POST', '/api/audit-logs/export', {
        event_type: filters.eventType,
        event_category: filters.eventCategory,
        user_id: filters.userId,
        institution_id: filters.institutionId,
        start_date: filters.startDate,
        end_date: filters.endDate,
        success: filters.success,
    }, { raw: true });

    if (!response.ok) {
        throw new Error('Failed to export audit logs');
    }

    return response.blob();
}

// ========== Audit Logs Types (SuperAdmin only) ==========

/**
 * Audit log entry from the API
 */
export interface AuditLogEntry {
    id: number;
    timestamp: number | null; // milliseconds since epoch
    userId: number | null;
    userEmail: string | null;
    eventType: string;
    eventCategory: string;
    ipAddress: string | null;
    userAgent: string | null;
    success: boolean;
    detail: string | null;
    resourceType: string | null;
    resourceId: number | null;
    institutionId: number | null;
}

/**
 * Filters for querying audit logs
 */
export interface AuditLogFilters {
    page?: number;
    perPage?: number;
    eventType?: string[];
    eventCategory?: string[];
    userId?: number;
    institutionId?: number;
    startDate?: string; // ISO 8601
    endDate?: string; // ISO 8601
    success?: boolean;
}

/**
 * Paginated audit log response
 */
export interface AuditLogResponse {
    items: AuditLogEntry[];
    total: number;
    page: number;
    perPage: number;
    pages: number;
}
